import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const SECTION_IDS = [
    "top",
    "problem",
    "solution",
    "product",
    "metrics",
    "dashboard",
    "pricing",
    "competitive",
    "traction",
    "team",
    "cta",
];

/**
 * Capture each pitch-deck section and assemble a multi-page landscape PDF.
 * Returns the jsPDF instance after triggering download.
 */
export async function exportDeckToPdf({ onProgress } = {}) {
    // Ensure all reveal animations are visible before snapshotting
    document.querySelectorAll(".reveal").forEach((el) => el.classList.add("in"));
    // Wait one frame so reveals paint
    await new Promise((r) => setTimeout(r, 200));

    const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
    });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    const totalSteps = SECTION_IDS.length;
    let step = 0;

    for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) {
            step += 1;
            onProgress?.({ step, total: totalSteps, label: id });
            continue;
        }

        // Ensure section is in viewport so charts render fully
        el.scrollIntoView({ behavior: "instant", block: "start" });
        await new Promise((r) => setTimeout(r, 350));

        const canvas = await html2canvas(el, {
            backgroundColor: "#04140D",
            scale: 1.5,
            useCORS: true,
            allowTaint: false,
            logging: false,
            windowWidth: 1440,
            onclone: (doc) => {
                // Force-show reveals + hide fixed/floating UI inside the clone
                doc.querySelectorAll(".reveal").forEach((n) =>
                    n.classList.add("in")
                );
                doc.querySelectorAll("header.no-print").forEach((n) => {
                    n.style.display = "none";
                });
                doc.querySelectorAll(
                    "[data-testid='presenter-controls'], [data-testid='presenter-exit'], [data-testid='presenter-slide-name'], [data-testid='presenter-fullscreen'], .marquee-track"
                ).forEach((n) => {
                    n.style.display = "none";
                });
            },
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.85);

        // Fit canvas inside page while preserving aspect ratio (letterbox)
        const cW = canvas.width;
        const cH = canvas.height;
        const ratio = Math.min(pageW / cW, pageH / cH);
        const drawW = cW * ratio;
        const drawH = cH * ratio;
        const offX = (pageW - drawW) / 2;
        const offY = (pageH - drawH) / 2;

        if (step > 0) pdf.addPage();
        pdf.setFillColor(4, 20, 13); // #04140D background
        pdf.rect(0, 0, pageW, pageH, "F");
        pdf.addImage(imgData, "JPEG", offX, offY, drawW, drawH, undefined, "FAST");

        // Footer slide number
        pdf.setFontSize(8);
        pdf.setTextColor(212, 255, 0); // #D4FF00
        pdf.text(
            `Davini.ai · ${String(step + 1).padStart(2, "0")} / ${String(totalSteps).padStart(2, "0")}`,
            24,
            pageH - 14
        );

        step += 1;
        onProgress?.({ step, total: totalSteps, label: id });
    }

    pdf.save("davini-ai-pitch-deck.pdf");

    // Scroll back to top
    window.scrollTo({ top: 0, behavior: "instant" });

    return pdf;
}
